import { Box, Stack, Typography, useTheme } from '@mui/material';
import { FC } from 'react';
import { useApiQuery } from '../../../../../query/hooks';
import { graphql } from '../../../../../gql';
import { FormattedMessage, useIntl } from 'react-intl';
import { ItemMultiSelect } from '../../../../../form/ItemMultiSelect';
import getOodColor from '../../../../../utils/getOodColor';
import { useTypedPathParams } from '../../../../../../../shared/fe/hooks/useTypedPathParams';
import { journeyDetailSchema } from '../../../JourneyDetailPage';
import { useModalContext } from '../../../../../modal/ModalContext';
import { JourneyTile } from '../../../../list/JourneyTile';

const widget_listJourney = graphql(`
	query widget_listJourney {
		listJourney(filter: { isTemplate: { eq: false } }) {
			id
			name
			color
			...Journey_SingleJourneyFragment
		}
	}
`);

type JourneysSelectProps = Readonly<{
	value: readonly string[];
	onChange(newTags: readonly string[]): void;
	activityId: string;
	size: 'medium' | 'small';
	hideAdd?: boolean;
}>;

export const JourneysSelect: FC<JourneysSelectProps> = ({ value, onChange, hideAdd, size, ...props }) => {
	const { data, isLoading } = useApiQuery(widget_listJourney);
	const { id: journeyId } = useTypedPathParams(journeyDetailSchema);
	const theme = useTheme();
	const intl = useIntl();
	const { setModal } = useModalContext();
	const journeysResult = data?.listJourney ?? [];

	const createSelectOptions = (currentJourneyId: string) => {
		const result = journeysResult
			.map((journey) => {
				return {
					label: journey.name,
					...journey,
				};
			})
			.filter((j) => j.id !== currentJourneyId);

		return result;
	};

	const options = createSelectOptions(journeyId);
	const journeys = value.filter((id) => options.some((o) => o.id === id)).map((id) => options.find((o) => o.id === id)!);

	const deleteConnectedJourneys = (journeyId: string) => {
		const newJourneys = value.filter((id) => id !== journeyId);
		onChange(newJourneys);
	};

	//NOTE: For now just redirect to JourneyCreateModal
	const handleJourneyCreate = () => {
		setModal({ type: 'createJourney', fullScreen: true });
	};

	if (isLoading) {
		return (
			<Typography sx={{ color: theme.palette.secondary.main }}>
				<FormattedMessage id={'common.loading'} />
			</Typography>
		);
	}

	return (
		<Stack direction={'column'} gap={2}>
			{journeys.length > 0 && (
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
					{journeys.map((journey) => (
						<JourneyTile
							key={journey.id}
							isShownOnWidget
							journeyData={journey}
							colorVariant={getOodColor(theme, journey.color)}
							onDelete={() => deleteConnectedJourneys(journey.id)}
						/>
					))}
				</Box>
			)}
			<Stack direction={'row'} sx={{ opacity: hideAdd ? 0 : 1 }} justifyContent={'space-between'} alignItems={'center'}>
				<Stack direction={'column'}>
					<ItemMultiSelect
						label={intl.formatMessage({ id: 'form.journeys-select.label' })}
						noItemsText={intl.formatMessage({ id: 'form.journeys-select.no-items' })}
						createText={intl.formatMessage({ id: 'form.journeys-select.create' })}
						emptyItemErrorText={intl.formatMessage({ id: 'form.journeys-select.errors.empty' })}
						createDialogTitle={intl.formatMessage({ id: 'form.journeys-select.title.create' })}
						isLoading={isLoading}
						onChange={onChange}
						defaultValue={value}
						options={options}
						onCreate={handleJourneyCreate}
						onRedirect={handleJourneyCreate}
					/>
				</Stack>
			</Stack>
		</Stack>
	);
};
